import React, { ReactNode } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Toolbar, Box, Typography, Divider, Avatar, IconButton } from '@mui/material';
import { Dashboard, AddCircle, CalendarToday, History, BarChart, Settings, AccountBalance } from '@mui/icons-material';
import WalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { StrictModeDroppable } from './StrictModeDroppable';
import { designTokens } from '../theme/designTokens';
import { User } from 'firebase/auth';

interface SidebarProps {
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  menuItems: Array<{ id: string; text: string; icon: ReactNode; value: string; }>;
  setMenuItems: React.Dispatch<React.SetStateAction<Array<{ id: string; text: string; icon: ReactNode; value: string; }>>>;
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  drawerWidth: number;
  user: User | null;
  onOpenSettings: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  menuItems,
  setMenuItems,
  mobileOpen,
  handleDrawerToggle,
  drawerWidth,
  user,
  onOpenSettings
}) => {
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(menuItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setMenuItems(items);
    localStorage.setItem('menuOrder', JSON.stringify(items.map(item => item.id)));
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: designTokens.colors.primary.main }}>
          <FormattedMessage id="app.title" />
        </Typography>
      </Box>
      <Divider />
      <DragDropContext onDragEnd={onDragEnd}>
        <StrictModeDroppable droppableId="menu">
          {(provided) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                flex: 1,
                padding: designTokens.spacing.sm,
                overflowY: 'auto'
              }}
            >
              {menuItems.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => (
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      button
                      onClick={() => {
                        setActiveTab(item.value);
                        if (mobileOpen) handleDrawerToggle();
                      }}
                      selected={activeTab === item.value}
                      sx={{
                        borderRadius: designTokens.borderRadius.md,
                        marginBottom: designTokens.spacing.xs,
                        transition: `all ${designTokens.transitions.fast}`,
                        '&.Mui-selected': {
                          background: designTokens.colors.primary.gradient,
                          color: 'white',
                          boxShadow: designTokens.shadows.md,
                          '&:hover': {
                            background: designTokens.colors.primary.gradient,
                            transform: 'translateX(4px)',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                        '&:hover': {
                          background: designTokens.colors.background.elevated,
                          transform: 'translateX(4px)',
                        },
                        '&:active': {
                          transform: 'translateX(2px)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: activeTab === item.value ? 'white' : designTokens.colors.text.secondary,
                          transition: `all ${designTokens.transitions.fast}`,
                        }}
                      >
                        {item.id === 'monthly' ? <CalendarToday /> :
                          item.id === 'income' ? <WalletIcon /> :
                            item.id === 'bankAccounts' ? <AccountBalance /> :
                              item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={<FormattedMessage id={`menu.${item.id}`} />}
                        primaryTypographyProps={{
                          fontSize: designTokens.typography.fontSize.sm,
                          fontWeight: activeTab === item.value
                            ? designTokens.typography.fontWeight.semibold
                            : designTokens.typography.fontWeight.normal,
                        }}
                      />
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </StrictModeDroppable>
      </DragDropContext>
      <Divider />
      {user && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{ width: 32, height: 32 }}
            src={user.photoURL || undefined}
            alt={user.displayName || 'User'}
          >
            {user.displayName?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 'medium' }}>
              {user.displayName}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onOpenSettings}>
            <Settings fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            background: designTokens.colors.background.paper,
            borderRight: `1px solid ${designTokens.colors.background.elevated}`,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            background: designTokens.colors.background.paper,
            borderRight: `1px solid ${designTokens.colors.background.elevated}`,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;